import { BarChartOutlined, LockOutlined, LoginOutlined, MailOutlined } from "@ant-design/icons";
import { Alert, Button, Checkbox, Form, Input } from "antd";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { forgotPassword, login, register } from "../api/authApi";
import { useAuthStore } from "../stores/authStore";
import { getApiErrorMessage } from "../shared/utils/apiError";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((x) => x.setSession);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const year = new Date().getFullYear();
  const mode = location.pathname.includes("register") ? "register" : location.pathname.includes("forgot-password") ? "forgot" : "login";
  const title = mode === "register" ? "Create account" : mode === "forgot" ? "Reset password" : "Welcome back";
  const subtitle = mode === "register"
    ? "Register a user account for AccountingSolution"
    : mode === "forgot"
      ? "Request password reset instructions"
      : "Sign in to your account to continue";

  return (
    <div className="auth-page">
      <div className="auth-brand-panel">
        <div className="auth-brand-logo">
          <div className="auth-brand-logo-icon"><BarChartOutlined /></div>
          <span className="auth-brand-logo-name">AccountingSolution</span>
        </div>

        <div className="auth-brand-content">
          <h1 className="auth-brand-headline">Smart Accounting<br />for Modern Business</h1>
          <p className="auth-brand-sub">
            Complete ERP solution to manage invoices, expenses, cash flow, GST compliance and
            financial reports from a single workspace.
          </p>
          <ul className="auth-brand-features">
            <li>Multi-tenant and multi-financial-year support</li>
            <li>Real-time KPI dashboard with smart caching</li>
            <li>GST-ready billing and voucher engine</li>
            <li>Role-based access control per organisation</li>
            <li>One-click PDF reports and Excel export</li>
          </ul>
        </div>

        <div className="auth-brand-footer">&copy; {year} AccountingSolution - All rights reserved</div>
      </div>

      <div className="auth-form-panel">
        <div className="auth-form-card">
          <div className="auth-mobile-logo">
            <div className="auth-mobile-logo-icon"><BarChartOutlined /></div>
            <span className="auth-mobile-logo-name">AccountingSolution</span>
          </div>

          <div className="auth-card-header">
            <h2 className="auth-card-title">{title}</h2>
            <p className="auth-card-subtitle">{subtitle}</p>
          </div>

          {error ? <Alert className="auth-alert" type="error" showIcon message={error} /> : null}
          {success ? <Alert className="auth-alert" type="success" showIcon message={success} /> : null}

          <Form
            key={mode}
            layout="vertical"
            onFinish={async (values) => {
              setLoading(true);
              setError(null);
              setSuccess(null);
              try {
                if (mode === "register") {
                  await register(values);
                  setSuccess("Registration completed. You can sign in now.");
                  navigate("/login");
                } else if (mode === "forgot") {
                  await forgotPassword(values.email);
                  setSuccess("Password reset request submitted. Check your email or contact your administrator.");
                } else {
                  const session = await login(values.email, values.password);
                  setSession(session);
                  navigate("/");
                }
              } catch (err) {
                setError(getApiErrorMessage(err, mode === "register" ? "Unable to register" : mode === "forgot" ? "Unable to request password reset" : "Unable to sign in"));
              } finally {
                setLoading(false);
              }
            }}
          >
            <Form.Item className="auth-field" name="email" label="Email address" rules={[{ required: true }]}>
              <Input prefix={<MailOutlined />} placeholder="you@company.com" autoComplete="username" autoFocus />
            </Form.Item>
            {mode === "register" ? (
              <>
                <Form.Item className="auth-field" name="firstName" label="First name"><Input /></Form.Item>
                <Form.Item className="auth-field" name="lastName" label="Last name"><Input /></Form.Item>
              </>
            ) : null}
            {mode !== "forgot" ? (
              <Form.Item className="auth-field" name="password" label="Password" rules={[{ required: true, min: 6 }]}>
                <Input.Password prefix={<LockOutlined />} placeholder="Enter your password" autoComplete={mode === "register" ? "new-password" : "current-password"} />
              </Form.Item>
            ) : null}
            {mode === "login" ? (
              <div className="auth-options">
                <Checkbox className="auth-remember">Remember me</Checkbox>
                <Link to="/forgot-password">Forgot password?</Link>
              </div>
            ) : null}
            <Button className="btn-auth-submit" type="primary" htmlType="submit" icon={<LoginOutlined />} loading={loading} block>
              {mode === "register" ? "Register" : mode === "forgot" ? "Send Reset Link" : "Sign In"}
            </Button>
          </Form>

          <div className="auth-card-footer">
            {mode === "login" ? <span>New user? <Link to="/register">Create account</Link></span> : <Link to="/login">Back to sign in</Link>}
          </div>
        </div>
        <div className="auth-page-footer">&copy; {year} AccountingSolution - All rights reserved</div>
      </div>
    </div>
  );
}
